/**
 * CSS Modulesのスタイルマッピングオブジェクト
 */
export interface CSSModulesStyles {
  readonly [className: string]: string;
}

/**
 * オブジェクトがCSSクラスマッピングを含むかどうかを厳密に検証（配列除外）
 */
const isValidStyleObject = (obj: unknown): obj is CSSModulesStyles => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  return Object.values(obj).every(value => typeof value === 'string');
};

/**
 * 開発環境のクライアントサイドでのみ動作するデバッグログ
 */
const debug = (message: string, data?: unknown): void => {
  const isDev =
    typeof process !== 'undefined' &&
    typeof process.env !== 'undefined' &&
    process.env.NODE_ENV === 'development';
  if (isDev && typeof window !== 'undefined') {
    console.log(`[extractStyle] ${message}`, data);
  }
};

/**
 * 異なるビルド環境間でCSS Modulesインポートを正規化
 *
 * 対応環境:
 * - Gatsby (Webpack + css-loader): 複雑なESM/CommonJS相互運用
 * - Astro (Vite): 直接クラス名マッピング
 * - SSR環境: localsプロパティパターン
 *
 * @param styleImport - CSS Modulesインポート結果
 * @returns 文字列クラス名を持つ正規化されたスタイルオブジェクト
 */
export const extractStyle = (styleImport: unknown): CSSModulesStyles => {
  // null/undefinedまたはオブジェクト以外の処理
  if (!styleImport || typeof styleImport !== 'object') {
    debug('falsy/非オブジェクトを受信', { value: styleImport });
    return {};
  }

  // Webpack ESM相互運用（最も一般的）
  if (
    'default' in (styleImport as any) &&
    isValidStyleObject((styleImport as any).default)
  ) {
    debug('defaultエクスポートを使用');
    return (styleImport as any).default;
  }

  // SSRツールチェイン
  if (
    'locals' in (styleImport as any) &&
    isValidStyleObject((styleImport as any).locals)
  ) {
    debug('localsプロパティを使用');
    return (styleImport as any).locals;
  }

  // 直接マッピング（Vite/Astro）
  if (isValidStyleObject(styleImport)) {
    debug('直接マッピングを使用');
    return styleImport;
  }

  // フォールバック: 未知の形式の場合は空オブジェクトを返す
  debug('未知の形式、空オブジェクトを返します', {
    keys: Object.keys(styleImport as object),
    hasDefault: 'default' in (styleImport as any),
    hasLocals: 'locals' in (styleImport as any)
  });

  return {};
};
